import React, { useEffect, useRef, useState } from "react";
import {
  fetchPhotos,
  createPhoto,
  updatePhoto,
  deletePhoto,
  imageUrl,
} from "../lib/photosApi";
import { processImage, isHeic, heicToImage } from "../lib/imageProcessing";
import Lightbox from "./lightbox";

const META_FIELDS = ["title", "category", "description", "location", "taken_at"];

const STAGE_LABELS = {
  queued: "Queued",
  converting: "Converting HEIC…",
  optimizing: "Optimizing…",
  uploading: "Uploading",
  done: "Done",
  error: "Failed",
};

const INDETERMINATE = new Set(["queued", "converting", "optimizing"]);

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const Dashboard = () => {
  const [photos, setPhotos] = useState([]);
  const [uploads, setUploads] = useState([]); // { id, name, status, progress, error }
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const mountedRef = useRef(true);
  const idRef = useRef(0);
  const dragDepth = useRef(0);
  const savedTimer = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    fetchPhotos()
      .then((data) => { if (mountedRef.current) setPhotos(data); })
      .catch(() => { if (mountedRef.current) setError("Could not load photos."); });
    return () => {
      mountedRef.current = false;
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  const patchUpload = (id, patch) => {
    if (!mountedRef.current) return;
    setUploads((list) => list.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter(
      (file) => file.type.startsWith("image/") || isHeic(file)
    );
    for (const file of files) {
      const id = (idRef.current += 1);
      const entry = { id, name: file.name, status: "queued", progress: 0, error: null };
      if (mountedRef.current) setUploads((u) => [entry, ...u]);
      try {
        let source = file;
        if (isHeic(file)) {
          patchUpload(id, { status: "converting" });
          source = await heicToImage(file);
        }
        patchUpload(id, { status: "optimizing" });
        const { full, grid, lqip, width, height } = await processImage(source);

        const fd = new FormData();
        fd.set("full", full, "full.webp");
        fd.set("grid", grid, "grid.webp");
        fd.set("orig", file);
        fd.set("lqip", lqip);
        fd.set("width", String(width));
        fd.set("height", String(height));

        patchUpload(id, { status: "uploading", progress: 0 });
        const created = await createPhoto(fd, (p) => patchUpload(id, { progress: p }));

        if (mountedRef.current) {
          setPhotos((p) => [...p, created]);
          patchUpload(id, { status: "done", progress: 1 });
        }
      } catch (err) {
        patchUpload(id, { status: "error", error: err.message });
      }
    }
  };

  const onInputChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };
  const onDragEnter = (e) => {
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const clearFinished = () =>
    setUploads((list) => list.filter((u) => u.status !== "done"));

  const saveField = async (photo, field, value) => {
    if ((photo[field] || "") === value) return;
    try {
      const updated = await updatePhoto(photo.id, { [field]: value || null });
      if (!mountedRef.current) return;
      setPhotos((p) => p.map((x) => (x.id === photo.id ? updated : x)));
      setSavedId(photo.id);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => {
        if (mountedRef.current) setSavedId(null);
      }, 1600);
    } catch {
      if (mountedRef.current) setError("Could not save changes.");
    }
  };

  const remove = async (photo) => {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    try {
      await deletePhoto(photo.id);
      if (mountedRef.current) setPhotos((p) => p.filter((x) => x.id !== photo.id));
    } catch {
      if (mountedRef.current) setError("Could not delete photo.");
    }
  };

  const hasFinished = uploads.some((u) => u.status === "done");

  return (
    <main className="dashboard-page">
      <header className="dashboard-head">
        <h1>Photo dashboard</h1>
        <span className="dashboard-count">{photos.length} in library</span>
      </header>
      {error && <p className="dashboard-error">{error}</p>}

      <section
        className={`dashboard-dropzone${isDragging ? " is-dragging" : ""}`}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          id="dashboard-file-input"
          className="dashboard-file-input"
          type="file"
          accept="image/*,.heic,.heif,image/heic,image/heif"
          multiple
          aria-label="Add photos"
          onChange={onInputChange}
        />
        <label htmlFor="dashboard-file-input" className="dashboard-dropzone-inner">
          <span className="dashboard-dropzone-icon" aria-hidden="true">
            ↑
          </span>
          <strong>Drop photos to upload</strong>
          <span className="dashboard-dropzone-hint">
            or click to browse — JPEG, PNG, WebP, HEIC · multiple at once
          </span>
        </label>
      </section>

      {uploads.length > 0 && (
        <section className="upload-tray" aria-label="Upload progress">
          <div className="upload-tray-head">
            <span>Uploads</span>
            {hasFinished && (
              <button type="button" className="upload-clear" onClick={clearFinished}>
                Clear finished
              </button>
            )}
          </div>
          <ul className="upload-list">
            {uploads.map((u) => {
              const pct = Math.round((u.progress || 0) * 100);
              const indeterminate = INDETERMINATE.has(u.status);
              return (
                <li key={u.id} className={`upload-item upload-${u.status}`}>
                  <div className="upload-meta">
                    <span className="upload-name" title={u.name}>
                      {u.name}
                    </span>
                    <span className="upload-stage">
                      {u.status === "uploading"
                        ? `Uploading ${pct}%`
                        : STAGE_LABELS[u.status]}
                    </span>
                  </div>
                  <div
                    className={`upload-bar${indeterminate ? " is-indeterminate" : ""}`}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={u.status === "done" ? 100 : pct}
                  >
                    <span
                      className="upload-bar-fill"
                      style={{
                        width: u.status === "done" ? "100%" : indeterminate ? undefined : `${pct}%`,
                      }}
                    />
                  </div>
                  {u.error && <p className="upload-error-msg">{u.error}</p>}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="dashboard-list">
        {photos.map((photo, index) => {
          const dimensions =
            photo.width && photo.height ? `${photo.width} × ${photo.height}` : null;
          const created = formatDate(photo.created_at);
          return (
            <article className="dashboard-row" key={photo.id}>
              <button
                type="button"
                className="dashboard-thumb-btn"
                aria-label={`View ${photo.title || "photo"} full screen`}
                onClick={() => setLightboxIndex(index)}
              >
                <img
                  className="dashboard-thumb"
                  src={imageUrl(photo.grid_key)}
                  alt={photo.title || ""}
                  style={{ backgroundImage: `url(${photo.lqip})` }}
                />
                <span className="dashboard-thumb-zoom" aria-hidden="true">
                  ⤢
                </span>
              </button>

              <div className="dashboard-fields-wrap">
                <div className="dashboard-meta">
                  {dimensions && <span>{dimensions}</span>}
                  {created && <span>Added {created}</span>}
                  {photo.taken_at && <span>Shot {photo.taken_at}</span>}
                  {savedId === photo.id && (
                    <span className="dashboard-saved" role="status">
                      Saved ✓
                    </span>
                  )}
                </div>
                <div className="dashboard-fields">
                  {META_FIELDS.map((field) => (
                    <label key={field} className="dashboard-field">
                      <span>{field.replace(/_/g, " ")}</span>
                      <input
                        type="text"
                        defaultValue={photo[field] || ""}
                        onBlur={(e) => saveField(photo, field, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="dashboard-actions">
                <button type="button" className="dashboard-delete" onClick={() => remove(photo)}>
                  Delete
                </button>
              </div>
            </article>
          );
        })}
      </section>

      {lightboxIndex !== null && (
        <Lightbox
          photos={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </main>
  );
};

export default Dashboard;
